export interface ImageDimensions {
    width: number;
    height: number;
}

export const CORPORATE_HEADSHOT_MIN_DIMENSION = 600;

// Video 2 fits a logo into its template box with FFmpeg
// `scale=W:H:force_original_aspect_ratio=decrease`, which UPSCALES a too-small
// logo (→ blurry). We require a minimum of 300×300 on both sides as a practical
// floor below which logos look noticeably soft in the generated video.
export const LOGO_MIN_WIDTH = 300;
export const LOGO_MIN_HEIGHT = 300;

export interface LogoValidationResult {
    ok: boolean;
    reason?: string;
}

export const validateLogoFile = async (
    file: File,
): Promise<LogoValidationResult> => {
    let dimensions: ImageDimensions;
    try {
        dimensions = await getImageDimensions(file);
    } catch {
        return {
            ok: false,
            reason: "Could not read this image. Try a different file.",
        };
    }
    const { width, height } = dimensions;
    if (width < LOGO_MIN_WIDTH || height < LOGO_MIN_HEIGHT) {
        return {
            ok: false,
            reason: `Logo is too small (${width}×${height}px). Use at least ${LOGO_MIN_WIDTH}×${LOGO_MIN_HEIGHT}px so it stays sharp in generated videos.`,
        };
    }
    return { ok: true };
};

export const getImageDimensions = async (file: File): Promise<ImageDimensions> => {
    // Materialize bytes upfront — on Android, File handles backed by content
    // URIs (e.g. screenshots picked from Gallery) can fail to feed an image element
    // through URL.createObjectURL(file) directly. Reading via arrayBuffer
    // forces the bytes to materialize; the resulting Blob is self-contained
    // and the same image then decodes the same way it does on desktop.
    const buf = await file.arrayBuffer();
    const blob = new Blob([buf], { type: file.type || "image/jpeg" });
    return new Promise<ImageDimensions>((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Failed to decode image"));
        };
        img.src = url;
    });
};
